import { useState } from 'react';
import { v4 as uuid } from 'uuid';
import { obtenerFechaHoyISO, formatearFechaLocal, normalizarFechaISO } from '../../../utils/horaLocal';
import { useModales } from './useModales';
import { calcularEfectivoYQR, calcularSaldosAcumuladosHasta } from '../services/cajaService';
import { construirTransaccionesDelDia } from '../services/transaccionesService';

export function useGeneralDelDia(state, gastosState) {
  const modales = useModales();

  // ===== FECHA =====
  const [fechaSeleccionada, setFechaSeleccionada] = useState(obtenerFechaHoyISO);
  const fechaDelDiaFormato = formatearFechaLocal(fechaSeleccionada);

  // ===== ESTADO MOVIMIENTOS =====
  const [tipoMov, setTipoMov] = useState('ENTRADA');
  const [subTipo, setSubTipo] = useState(null);
  const [metodoPago, setMetodoPago] = useState('EFECTIVO');
  const [filaActual, setFilaActual] = useState({ prodId: '', cant: '', precio: '', total: '' });
  const [carrito, setCarrito] = useState([]);

  // ===== ESTADO EDICIÓN =====
  const [transaccionEnEdicion, setTransaccionEnEdicion] = useState(null);
  const [valoresEdicion, setValoresEdicion] = useState({});

  // ===== ESTADO GASTO =====
  const [gasto, setGasto] = useState({ categoria: '', monto: '', metodoPago: 'EFECTIVO' });

  // ===== ESTADO AJUSTE =====
  const [tipoAjuste, setTipoAjuste] = useState('SOBRANTE');
  const [montoAjuste, setMontoAjuste] = useState('');
  const [descripcionAjuste, setDescripcionAjuste] = useState('');

  // ===== ESTADO TRASPASO =====
  const [montoTraspaso, setMontoTraspaso] = useState('');
  const [direccionTraspaso, setDireccionTraspaso] = useState('CAJ_A_QR');
  const [razonTraspaso, setRazonTraspaso] = useState('');

  // ===== ESTADO FONDOS =====
  const [tipoFondo, setTipoFondo] = useState('INGRESO');
  const [montoFondo, setMontoFondo] = useState('');
  const [descripcionFondo, setDescripcionFondo] = useState('');
  const [metodoPagoFondos, setMetodoPagoFondos] = useState('EFECTIVO');

  // ===== DERIVADOS =====
  const díaBloqueado = state?.verificarDíaBloqueado(fechaSeleccionada);
  const arqueoDelDía = state?.obtenerArqueoDelDía(fechaSeleccionada);

  // ===== DATOS DEL DÍA =====
  // Movimiento, ajuste y traspaso vienen de state que se actualiza internament
  const movimientosDia = state.obtenerMovimientosPorFecha(fechaSeleccionada, true);
  const movimientosDiaActivos = movimientosDia.filter(m => !m.anulado);
  const ajustesDia = (state.ajustes || []).filter(a => formatearFechaLocal(normalizarFechaISO(a.fecha)) === formatearFechaLocal(normalizarFechaISO(fechaSeleccionada)));
  const ajustesDiaActivos = ajustesDia.filter(a => !state.esAjusteAnulado(a));

  // Gastos: usar gastosState directamente para que se actualice en TIEMPO REAL
  // cuando se registran nuevos gastos desde ModalGastos
  const formatoFechaActual = formatearFechaLocal(normalizarFechaISO(fechaSeleccionada));
  const gastosDia = (gastosState?.gastos || []).filter(g => {
    const fechaGasto = formatearFechaLocal(normalizarFechaISO(g.fecha));
    return fechaGasto === formatoFechaActual;
  });
  const gastosDiaActivos = gastosDia.filter(g => g.estado !== 'ANULADO');
  const traspasosDia = (state.traspasosQR || []).filter(t => {
    return formatearFechaLocal(normalizarFechaISO(t.fecha)) === formatoFechaActual;
  });

  const transacciones = construirTransaccionesDelDia({
    movimientos: movimientosDia,
    gastos: gastosDia,
    ajustes: ajustesDia,
    traspasos: traspasosDia,
    productos: state.productos,
  });

  const traspasosDiaActivos = traspasosDia.filter(t => !t.anulado);
  const { totalEfectivo, totalQR } = calcularEfectivoYQR({
    movimientos: movimientosDiaActivos,
    gastos: gastosDiaActivos,
    ajustes: ajustesDiaActivos,
    traspasos: traspasosDiaActivos,
  });

  // Saldos de inicio del día = cierre del día anterior
  const fechaLimiteNormalizada = normalizarFechaISO(fechaSeleccionada);
  const { inicioEfectivo, inicioQR } = calcularSaldosAcumuladosHasta({
    movimientos: state.movimientos || [],
    gastos: gastosState?.gastos || [],
    ajustes: state.ajustes || [],
    traspasos: state.traspasosQR || [],
    fechaLimite: fechaLimiteNormalizada,
    normalizarFecha: normalizarFechaISO,
  });

  // ===== CÁLCULO DINÁMICO DE DATOS DEL DÍA =====
  // Se recalcula cada vez que cambian gastosDia, movimientosDia o ajustesDia
  const datosDelDia = (() => {
    let datos = {
      fecha: fechaDelDiaFormato,
      compras: 0,
      ventas: 0,
      costos: 0,
      gastos: 0,
      ajustes: 0,
      ajustesEfectivo: 0,
      ajustesQR: 0,
      ganancia: 0,
      detalles: [],
    };

    // Calcular ventas y costos (COSTO DE MERCANCÍA VENDIDA)
    // Usa precioFijado (congelado al momento de la venta) para auditoría e inmutabilidad
    // Si no existe precioFijado, usa cálculo antiguo (backward compatibility)
    (movimientosDiaActivos || [])
      .filter(m => m.tipo === 'VENTA' || (m.tipo === 'SALIDA' && m.subTipo === 'VENTA'))
      .forEach(venta => {
        const ingreso = venta.cantidad * venta.precio;
        const precioParaCosto = state.calcularCostoMovimiento(venta);
        const costo = venta.cantidad * precioParaCosto;
        
        datos.ventas += ingreso;
        datos.costos += costo;
        datos.ganancia += (ingreso - costo);
      });

    // Calcular compras
    (movimientosDiaActivos || [])
      .filter(m => m.tipo === 'ENTRADA')
      .forEach(entrada => {
        datos.compras += entrada.cantidad * entrada.precio;
      });

    // Calcular costo de SALIDA simple (reduce inventario físico y valorado)
    (movimientosDiaActivos || [])
      .filter(m => m.tipo === 'SALIDA' && m.subTipo !== 'VENTA')
      .forEach(salida => {
        const precioParaCosto = state.calcularCostoMovimiento(salida);
        const costoSalida = salida.cantidad * precioParaCosto;
        datos.costos += costoSalida;
        datos.ganancia -= costoSalida;
      });

    // Calcular gastos (REACTIVO A CAMBIOS EN gastosDia)
    (gastosDiaActivos || [])
      .forEach(gasto => {
        datos.gastos += Number(gasto.monto) || 0;
        datos.ganancia -= Number(gasto.monto) || 0;
      });

    // Calcular ajustes (separados por método de pago)
    (ajustesDiaActivos || [])
      .forEach(ajuste => {
        const montoAjuste = ajuste.tipo === 'SOBRANTE' || ajuste.tipo === 'INYECCIÓN' ? ajuste.monto : -ajuste.monto;
        datos.ajustes += montoAjuste;
        if (['SOBRANTE', 'FALTANTE'].includes(ajuste.tipo)) datos.ganancia += montoAjuste;
        const metodo = (ajuste.metodoPago || 'EFECTIVO').toUpperCase();
        if (metodo === 'QR') {
          datos.ajustesQR += montoAjuste;
        } else {
          datos.ajustesEfectivo += montoAjuste;
        }
      });

    return datos;
  })();

  // ===== HANDLERS MOVIMIENTOS =====
  const handleAbrirMovimientos = (tipo) => {
    setTipoMov(tipo);
    setSubTipo(null);
    setCarrito([]);
    setMetodoPago('EFECTIVO');
    setFilaActual({ prodId: '', cant: '', precio: '', total: '' });
    modales.abrir('MOVIMIENTO');
  };

  const handleAbrirVenta = () => {
    setTipoMov('VENTA');
    setSubTipo(null);
    setCarrito([]);
    setMetodoPago('EFECTIVO');
    setFilaActual({ prodId: '', cant: '', precio: '', total: '' });
    modales.abrir('MOVIMIENTO');
  };

  const handleAgregarAlCarrito = () => {
    if (!filaActual.prodId || !filaActual.cant) {
      alert('Selecciona producto y cantidad');
      return;
    }
    const producto = state.productos.find(p => p.id === filaActual.prodId);
    setCarrito(prev => [...prev, {
      prodId: filaActual.prodId,
      producto: producto?.nombre,
      cant: parseFloat(filaActual.cant),
      precio: parseFloat(filaActual.precio),
      total: parseFloat(filaActual.total),
    }]);
    setFilaActual({ prodId: '', cant: '', precio: '', total: '' });
  };

  const handleEliminarDelCarrito = (index) => {
    setCarrito(prev => prev.filter((_, i) => i !== index));
  };

  const handleRegistrarMovimientos = async () => {
    if (carrito.length === 0) { alert('El carrito está vacío'); return; }
    if (díaBloqueado) { alert('⛔ No puedes registrar movimientos cuando hay un arqueo completado en el día'); return; }

    const transactionID = uuid();
    let contador = 0;
    for (const item of carrito) {
      if (await state.registrarMovimiento(item.prodId, item.cant, item.precio, tipoMov, transactionID, subTipo, metodoPago, fechaSeleccionada)) {
        contador++;
      }
    }

    if (contador > 0) {
      alert(`${contador} producto(s) registrado(s)`);
      modales.cerrar();
      setCarrito([]);
      setFilaActual({ prodId: '', cant: '', precio: '', total: '' });
    }
  };

  // ===== HANDLERS GASTO =====
  const handleAbrirGasto = () => {
    if (díaBloqueado) { alert('⛔ No puedes registrar gastos cuando hay un arqueo completado en el día'); return; }
    setGasto({ categoria: '', monto: '', metodoPago: 'EFECTIVO' });
    modales.abrir('GASTO');
  };

  const handleAbrirLimpieza = () => {
    if (díaBloqueado) { alert('⛔ No puedes registrar limpiezas cuando hay un arqueo completado en el día'); return; }
    modales.abrir('LIMPIEZA');
  };

  const handleRegistrarGasto = async () => {
    modales.cerrar();
  };

  // ===== HANDLERS AJUSTE =====
  const handleAbrirAjuste = () => {
    if (díaBloqueado) { alert('⛔ No puedes crear ajustes cuando hay un arqueo completado en el día'); return; }
    setTipoAjuste('SOBRANTE');
    setMontoAjuste('');
    setDescripcionAjuste('');
    modales.abrir('AJUSTE');
  };

  const handleRegistrarAjuste = async () => {
    if (!montoAjuste || isNaN(montoAjuste) || parseFloat(montoAjuste) <= 0) { alert('Monto inválido'); return; }
    const success = await state.registrarAjusteManual(
      tipoAjuste,
      parseFloat(montoAjuste),
      descripcionAjuste,
      'EFECTIVO',
      fechaSeleccionada
    );
    if (success) {
      alert('Ajuste registrado');
      modales.cerrar();
      setMontoAjuste('');
      setDescripcionAjuste('');
    }
  };

  // ===== HANDLERS TRASPASO =====
  const handleAbrirTraspaso = () => {
    if (díaBloqueado) { alert('⛔ No puedes registrar traspasos cuando hay un arqueo completado en el día'); return; }
    setMontoTraspaso('');
    setDireccionTraspaso('CAJ_A_QR');
    setRazonTraspaso('');
    modales.abrir('TRASPASO');
  };

  const handleRegistrarTraspaso = async () => {
    if (!montoTraspaso || isNaN(montoTraspaso) || parseFloat(montoTraspaso) <= 0) { alert('Monto inválido'); return; }
    const success = await state.traspasoQRCaja(parseFloat(montoTraspaso), direccionTraspaso, fechaSeleccionada);
    if (success) {
      alert('Traspaso registrado');
      modales.cerrar();
      setMontoTraspaso('');
      setRazonTraspaso('');
    }
  };

  // ===== HANDLERS ARQUEO =====
  const handleRegistrarArqueo = async (nuevoArqueo) => {
    // NO cerrar aquí — el modal maneja su propio flujo (resultado → ajuste → cierre)
    const created = await state.guardarArqueo(nuevoArqueo);
    return created || nuevoArqueo;
  };

  const handleRegistrarAjustePorArqueo = async (nuevoAjuste) => {
    await state.guardarAjuste(nuevoAjuste);
    modales.cerrar();
  };

  const handleAnularArqueo = async (id) => {
    await state.anularArqueo(id);
    await state.anularAjustesPorArqueo(id);
    modales.cerrar();
  };

  // ===== HANDLERS FONDOS =====
  const handleAbrirFondos = () => {
    if (díaBloqueado) { alert('⛔ No puedes registrar movimientos cuando hay un arqueo completado en el día'); return; }
    setTipoFondo('INGRESO');
    setMontoFondo('');
    setDescripcionFondo('');
    setMetodoPagoFondos('EFECTIVO');
    modales.abrir('FONDOS');
  };

  const handleRegistrarFondos = async () => {
    if (!montoFondo || isNaN(montoFondo) || parseFloat(montoFondo) <= 0) { alert('Monto inválido'); return; }
    const success = await state.registrarAjusteManual(
      tipoFondo === 'INGRESO' ? 'INYECCIÓN' : 'RETIRO',
      parseFloat(montoFondo),
      descripcionFondo || (tipoFondo === 'INGRESO' ? 'Ingreso de fondos externos' : 'Retiro de fondos'),
      metodoPagoFondos,
      fechaSeleccionada
    );
    if (success) {
      alert(`Fondo ${tipoFondo === 'INGRESO' ? 'ingresado' : 'retirado'} correctamente`);
      modales.cerrar();
      setMontoFondo('');
      setDescripcionFondo('');
    }
  };

  // ===== HANDLER ANULAR TRANSACCIÓN =====
  const handleAnularTransaccion = async (transaccion) => {
    let anulado = false;
    if (transaccion.esMovimiento) {
      anulado = await state.anularMovimiento(transaccion.id);
    } else if (transaccion.esGasto) {
      anulado = await gastosState.anularGasto(transaccion.id);
    } else if (transaccion.esAjuste) {
      anulado = await state.anularAjuste(transaccion.id);
    } else if (transaccion.esTraspaso) {
      anulado = await state.anularTraspaso(transaccion.id);
    }
    
    if (!anulado) return;
  };

  // ===== HANDLER EDITAR FECHA =====
  const handleEditarFecha = async (transaccion, nuevaFecha) => {
    if (transaccion.esMovimiento) {
      await state.editarFechaMovimiento(transaccion.id, nuevaFecha);
    } else if (transaccion.esGasto) {
      await gastosState.editarFechaGasto(transaccion.id, nuevaFecha);
    } else if (transaccion.esAjuste) {
      await state.editarFechaAjuste(transaccion.id, nuevaFecha);
    } else if (transaccion.esTraspaso) {
      await state.editarFechaTraspaso(transaccion.id, nuevaFecha);
    }
  };

  // ===== HANDLERS EDICIÓN =====
  const iniciarEdicion = (transaccion) => {
    if (!transaccion.puedeEditar) return;
    setTransaccionEnEdicion(transaccion.id);
    if (transaccion.esMovimiento) {
      setValoresEdicion({
        cantidad: transaccion.cantidad.toString(),
        precio: transaccion.precio.toString(),
        total: (transaccion.cantidad * transaccion.precio).toFixed(2),
      });
    } else if (transaccion.esGasto) {
      setValoresEdicion({
        monto: transaccion.monto.toString(),
        descripcion: transaccion.objOriginal.descripcion || '',
      });
    } else if (transaccion.esAjuste) {
      setValoresEdicion({
        monto: transaccion.monto.toString(),
        descripcion: transaccion.objOriginal.descripcion || '',
      });
    } else if (transaccion.esTraspaso) {
      setValoresEdicion({
        monto: transaccion.monto.toString(),
      });
    }
  };

  const handleCambioMovimiento = (campo, valor) => {
    const cantidad = parseFloat(valoresEdicion.cantidad || 0);
    const precio = parseFloat(valoresEdicion.precio || 0);
    const nuevosValores = { ...valoresEdicion };

    if (campo === 'cantidad') {
      nuevosValores.cantidad = valor;
      if (precio > 0) nuevosValores.total = ((parseFloat(valor) || 0) * precio).toFixed(2);
    } else if (campo === 'precio') {
      nuevosValores.precio = valor;
      if (cantidad > 0) nuevosValores.total = (cantidad * (parseFloat(valor) || 0)).toFixed(2);
    } else if (campo === 'total') {
      nuevosValores.total = valor;
      if (cantidad > 0) nuevosValores.precio = ((parseFloat(valor) || 0) / cantidad).toFixed(2);
    }
    setValoresEdicion(nuevosValores);
  };

  const cambiarValorEdicion = (campo, valor) => {
    setValoresEdicion(prev => ({ ...prev, [campo]: valor }));
  };

  const cancelarEdicion = () => {
    setTransaccionEnEdicion(null);
    setValoresEdicion({});
  };

  const guardarEdicion = async (transaccion) => {
    try {
      if (transaccion.esMovimiento) {
        const cantidad = parseFloat(valoresEdicion.cantidad);
        const precio = parseFloat(valoresEdicion.precio);
        if (isNaN(cantidad) || cantidad <= 0) { alert('La cantidad debe ser mayor a 0'); return; }
        if (isNaN(precio) || precio < 0) { alert('El precio no puede ser negativo'); return; }
        await state.editarMovimiento(transaccion.id, cantidad, precio);
      } else if (transaccion.esGasto) {
        const monto = parseFloat(valoresEdicion.monto);
        if (isNaN(monto) || monto <= 0) { alert('El monto debe ser mayor a 0'); return; }
        await gastosState.editarGasto(
          transaccion.id, monto,
          transaccion.objOriginal.categoriaId,
          valoresEdicion.descripcion,
          transaccion.objOriginal.metodoPago
        );
      } else if (transaccion.esAjuste) {
        const monto = parseFloat(valoresEdicion.monto);
        if (isNaN(monto) || monto <= 0) { alert('El monto debe ser mayor a 0'); return; }
        await state.editarAjuste(transaccion.id, monto);
      } else if (transaccion.esTraspaso) {
        const monto = parseFloat(valoresEdicion.monto);
        if (isNaN(monto) || monto <= 0) { alert('El monto debe ser mayor a 0'); return; }
        await state.editarTraspaso(transaccion.id, monto);
      }
      setTransaccionEnEdicion(null);
      setValoresEdicion({});
    } catch (error) {
      console.error('Error al guardar edición:', error);
      alert('Error al guardar los cambios');
    }
  };

  return {
    // Fecha
    fechaSeleccionada, setFechaSeleccionada, fechaDelDiaFormato,
    // Modal manager
    modales,
    // Estado formularios
    tipoMov, subTipo, metodoPago, setMetodoPago,
    filaActual, setFilaActual,
    carrito,
    gasto, setGasto,
    tipoAjuste, setTipoAjuste,
    montoAjuste, setMontoAjuste,
    descripcionAjuste, setDescripcionAjuste,
    montoTraspaso, setMontoTraspaso,
    direccionTraspaso, setDireccionTraspaso,
    razonTraspaso, setRazonTraspaso,
    tipoFondo, setTipoFondo,
    montoFondo, setMontoFondo,
    descripcionFondo, setDescripcionFondo,
    metodoPagoFondos, setMetodoPagoFondos,
    // Derivados
    díaBloqueado, arqueoDelDía,
    // Datos computados
    transacciones, totalEfectivo, totalQR, datosDelDia, inicioEfectivo, inicioQR,
    // Edición
    transaccionEnEdicion, valoresEdicion,
    iniciarEdicion, cancelarEdicion, guardarEdicion,
    handleCambioMovimiento, cambiarValorEdicion,
    // Anular y editar fecha
    handleAnularTransaccion, handleEditarFecha,
    // Handlers
    handleAbrirMovimientos, handleAbrirVenta,
    handleAgregarAlCarrito, handleEliminarDelCarrito, handleRegistrarMovimientos,
    handleAbrirGasto, handleRegistrarGasto,
    handleAbrirLimpieza,
    handleAbrirAjuste, handleRegistrarAjuste,
    handleAbrirTraspaso, handleRegistrarTraspaso,
    handleRegistrarArqueo, handleRegistrarAjustePorArqueo, handleAnularArqueo,
    handleAbrirFondos, handleRegistrarFondos,
  };
}
